"use client"

import type React from "react"

import { useMemo } from "react"
import { Box, Paper, Typography, Stack, Divider, IconButton, Tooltip, Card, useTheme } from "@mui/material"
import { ChevronLeft, ChevronRight, Close as CloseIcon } from "@mui/icons-material"
import type { SegmentPart } from "./lib/types"
import { SegmentPartItem } from "./segment-part-item"
import type { DragData } from "./lib/hooks/use-drag-drop"
import { sidebarCollapsedStyles, sidebarExpandedStyles, sidebarHeaderSecondaryStyles, sidebarToggleButtonStyles, sidebarScrollbarStyles, iconButtonSmallStyles } from "./schedular-styles"

interface SegmentPartsSidebarProps {
  segmentParts: SegmentPart[]
  workOrders: any[]
  onDragStart: (e: React.DragEvent, data: DragData) => void
  onDragEnd: (e: React.DragEvent) => void
  onRemoveBreakdown?: (workOrderId: string) => void
  collapsed?: boolean
  onToggleCollapse?: () => void
}

export function SegmentPartsSidebar({
  segmentParts,
  workOrders,
  onDragStart,
  onDragEnd,
  onRemoveBreakdown,
  collapsed = false,
  onToggleCollapse,
}: SegmentPartsSidebarProps) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const formatHours = (hours: number) => {
    const totalMinutes = Math.round(hours * 60)
    const h = Math.floor(totalMinutes / 60)
    const m = totalMinutes % 60
    return `${h}.${m.toString().padStart(2, "0")}`
  }

  const getWorkOrderName = (workOrderId: string) => {
    return workOrders.find((wo) => wo.id === workOrderId)?.name || "Unknown"
  }

  // Get work order number for display (matches format used in work-order-item and technician-card)
  const getWorkOrderNumber = (workOrderId: string) => {
    const workOrder = workOrders.find((wo) => wo.id === workOrderId)
    return workOrder?.invoiceNumber || "Work Order"
  }

  // CRITICAL: Get segment name from parentSegmentId
  // This ensures segment names are displayed correctly in the sidebar
  const getSegmentName = (segmentId: string, workOrderId: string) => {
    const workOrder = workOrders.find((wo) => wo.id === workOrderId)
    if (!workOrder) return "Unknown Segment"
    
    const segment = workOrder.segmentList?.find((seg: any) => seg.id === segmentId)
    if (!segment) return "Unknown Segment"
    
    // Handle multiple possible field names for segment name
    return segment.name || 
           segment.DisplayText || 
           segment.displayText || 
           segment.segmentName || 
           segment.SegmentName || 
           `Segment ${segmentId}`
  }

  const groupedParts = useMemo(() => {
    // Group by parentSegmentId (which groups parts from the same breakdown)
    return segmentParts
      .filter((part) => !part.isUsed) // Only show unused parts
      .reduce(
        (acc, part) => {
          const segmentId = part.parentSegmentId
          if (!acc[segmentId]) acc[segmentId] = []
          acc[segmentId].push(part)
          return acc
        },
        {} as Record<string, SegmentPart[]>,
      )
  }, [segmentParts])

  if (collapsed) {
    return (
      <Paper elevation={0} sx={sidebarCollapsedStyles}>
        <Tooltip title="Expand Segments" placement="left">
          <IconButton onClick={onToggleCollapse} sx={sidebarToggleButtonStyles}>
            <ChevronLeft />
          </IconButton>
        </Tooltip>
      </Paper>
    )
  }

  return (
    <Paper elevation={0} sx={sidebarExpandedStyles}>
      <Box
        sx={{
          ...sidebarHeaderSecondaryStyles,
          bgcolor: isDarkMode ? "#424242" : "primary.main" // Match work-orders-sidebar color scheme
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5, p: 1}}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "white" }}>
            Broken Down Segments
          </Typography>
          <Tooltip title="Collapse Sidebar" placement="right">
            <IconButton
              size="small"
              onClick={onToggleCollapse}
              sx={{
                ...sidebarToggleButtonStyles,
                color: "white",
                "&:hover": {
                  bgcolor: isDarkMode
                    ? "rgba(255, 255, 255, 0.15)"
                    : "rgba(255, 255, 255, 0.1)",
                  color: "white",
                },
              }}
            >
              <ChevronRight />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Stack sx={sidebarScrollbarStyles}>
        {Object.entries(groupedParts).length === 0 ? (
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Break down segments into variable sized parts
            </Typography>
          </Box>
        ) : (
          Object.entries(groupedParts).map(([segmentId, parts], idx) => {
            const workOrderId = parts[0]?.parentWorkOrderId || ""
            const totalHours = parts.reduce((sum, part) => sum + part.hours, 0)
            const workOrderName = getWorkOrderName(workOrderId)
            const workOrderNumber = getWorkOrderNumber(workOrderId)
            const segmentName = getSegmentName(segmentId, workOrderId)
            
            // Get work order details for drag data
            const workOrder = workOrders.find((wo) => wo.id === workOrderId)

            return (
              <Box key={segmentId}>
                {idx > 0 && <Divider sx={{ my: 2 }} />}
                <Card
                  elevation={2}
                  sx={{
                    p: 2,
                    mb: 1.5,
                    border: 2,
                    borderColor: "primary.light",
                    borderRadius: 2,
                    position: "relative",
                    bgcolor: "background.paper",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 1.5,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          color: "text.primary",
                          display: "block",
                          mb: 0.5,
                        }}
                      >
                        {segmentName}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          display: "block",
                          mb: 0.5,
                        }}
                      >
                        {workOrderNumber}
                      </Typography>
                      <Typography
                          component="span"
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: "primary.main",
                            fontSize: "0.875rem",
                          }}
                        >
                          {formatHours(totalHours)}h 
                        </Typography>
                      
                    </Box>
                    {onRemoveBreakdown && (
                      <IconButton
                        size="small"
                        onClick={() => onRemoveBreakdown(workOrderId)}
                        sx={iconButtonSmallStyles}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  <Stack spacing={1}>
                    {parts.map((part, index) => (
                      <SegmentPartItem
                        key={part.id}
                        part={part}
                        parentWorkOrderName={workOrderName}
                        segmentName={segmentName}
                        partNumber={index + 1}
                        onDragStart={onDragStart}
                        onDragEnd={onDragEnd}
                        // Work order details for drag data
                        workOrderNumber={workOrder?.workOrderNumber}
                        invoiceNumber={workOrder?.invoiceNumber}
                        customerName={workOrder?.customerName}
                        customerNumber={workOrder?.customerNumber}
                        unitNumber={workOrder?.unitNumber}
                        unitModel={workOrder?.unitModel}
                        vinNumber={workOrder?.vinNumber}
                        myReference={workOrder?.myReference}
                        woStatusId={workOrder?.woStatusId}
                        woDescription={workOrder?.description}
                        woPriorityId={workOrder?.woPriorityId}
                      />
                    ))}
                  </Stack>
                </Card>
              </Box>
            )
          })
        )}
      </Stack>
    </Paper>
  )
}
